-- | Modal form for entering or drawing a signature.
-- |
-- | Gathers signer details and the signature image. The app runtime submits the
-- | sign request after the modal returns.
module Widgets.Signature.Form where

import Common

import API.Signature (SignOpts)
import API.Types (DocumentID)
import Concur.Core.Patterns (loopState)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Data.Maybe (maybe)
import GraphQL.Type (innerStr)
import Internal.ConcurUtil (loopInfinitely)
import Widgets.Checkbox (labelCheckbox)
import Widgets.Signature.Pad (canvasHasInk, clearCanvas, getCanvasBlob, initialSignature, signaturePad)
import Widgets.Signature.Types (FormSignature, PartialSignature, SignatureField, addSignature)


type SigFormState =
    { field :: SignatureField
    , formId :: DocumentID
    , sig :: PartialSignature
    -- ^ Contains the signature data.
    -- ^ Note: the signature image blob in here
    -- ^ will only be used to initialize the pad, and won't be updated until the
    -- ^ user clicks sign.
    , msg :: Maybe String
    , save :: Boolean
    }

-- | Initializes the signature form based on the given existing signature data.
initSigFormState :: DocumentID -> SignatureField -> PartialSignature -> SigFormState
initSigFormState formId field sig = { field, formId, sig, msg: Nothing, save: false }

-- | Runs the signature form and then returns the signature data that was entered.
signatureForm :: SigFormState -> Widget HTML SigFormResult
signatureForm state = loopState state runSigForm

data SigFormSubmit
    = SubmitSignature FormSignature SignOpts
    | SubmitSavedSignature

type SigFormResult = Either ErrorWrapper SigFormSubmit


data SigFormAction
    = FinishSigning
    | ClearSignature
    | SignWithSaved

runSigForm :: SigFormState -> Widget HTML (Either SigFormState SigFormResult)
runSigForm state = do
    res <- renderSigForm state
    case res of
        Right act -> handleSFAction state act
        Left state' -> pure $ Left state'

validateSigForm :: SigFormState -> Boolean
validateSigForm { sig: { first_name, last_name, email } } =
    (first_name /= "") && (last_name /= "") && (email /= "")

-- | Handles actions from the signature form and returns either the next form state or the final submit result.
-- |
-- | 1. Validate the typed signer fields before inspecting the canvas.
-- | 2. Check that the canvas contains ink before asking it for a blob.
-- | 3. Build the submitted signature only after both checks pass.
-- | 4. Handle saved-signature and clear actions directly.
handleSFAction :: SigFormState -> SigFormAction -> Widget HTML (Either SigFormState SigFormResult)
handleSFAction state@{ sig } FinishSigning =
    if not $ validateSigForm state
        then pure $ Left $
            state { msg = Just $ lookupTranslation "signature-form-invalid" }
        else do 
            hasInk <- liftEffect $ canvasHasInk $ innerStr state.field.key
            if not hasInk
                then pure $ Left $
                    state { msg = Just $ lookupTranslation "signature-form-invalid" }
                else do
                    blob <- (affAction $ getCanvasBlob $ innerStr state.field.key) <|>
                            (loopInfinitely $ renderSigForm state)
                    let sig' = addSignature sig blob
                    let opts = { saveDefault: state.save }
                    pure $ Right $ Right $ SubmitSignature sig' opts

handleSFAction _ SignWithSaved =
    pure $ Right $ Right SubmitSavedSignature

handleSFAction state ClearSignature = do
    liftEffect $ clearCanvas $ innerStr state.field.key
    pure $ Left $ state { sig { signature = Nothing } }

renderSigForm :: SigFormState -> Widget HTML (Either SigFormState SigFormAction)
renderSigForm state@{ sig } = D.form
    [ P.className "signature-form modal-grid" ] $
    (if not state.field.can.useDefault then [] else
    [ D.button
        [ P.className "btn signature-insert-default", P._type "button"
        , Right SignWithSaved <$ P.onClick
        ]
        [ D.text $ lookupTranslation "sig-form-sign-with-saved" ] ])
    <>
    [ D.div
        [ P.className "signature-form-pad-container" ]
        [ signaturePad
            ([ P.className "signature-form-pad-canvas"
            , P._id $ innerStr state.field.key
            , P.key (innerStr state.field.key <> "-sigpad")
            ] <> maybe [] (\blob -> [ initialSignature blob ]) sig.signature)
        , D.button
            [ Right ClearSignature <$ P.onClick, P.className "signature-clear btn", P._type "button" ]
            [ D.text $ lookupTranslation "clear-lowercase" ]
        ]
    , D.div [ P.className "signature-form-info-container message" ] maybeMsg
    , D.div
        [ P.className "signature-form-info-container" ] $
        [ D.input
            [ P.key (innerStr state.field.key <> "-first-name")
            , P._id (innerStr state.field.key <> "-first-name")
            , P.className "widget-input"
            , P.placeholder $ lookupTranslation "first-name"
            , P.value sig.first_name
            , Left <$> state { sig { first_name = _ } } <$> P.unsafeTargetValue <$> P.onChange
            ]
        , D.input
            [ P.key (innerStr state.field.key <> "-last-name")
            , P._id (innerStr state.field.key <> "-last-name")
            , P.className "widget-input"
            , P.placeholder $ lookupTranslation "last-name"
            , P.value sig.last_name
            , Left <$> state { sig { last_name = _ } } <$> P.unsafeTargetValue <$> P.onChange
            ]
        , D.input
            [ P.key (innerStr state.field.key <> "-email")
            , P._id (innerStr state.field.key <> "-email")
            , P.className "widget-input"
            , P.autoComplete "email"
            , P.placeholder $ lookupTranslation "email"
            , P.value sig.email
            , Left <$> state { sig { email = _ } } <$> P.unsafeTargetValue <$> P.onChange
            ]
        ] <> (if not state.field.can.saveDefault then [] else
        [ Left <$> state { save = _ } <$> labelCheckbox (innerStr state.field.key <> "-savecheck")
            (lookupTranslation "save-signature") state.save
        ])
    , D.div
        [ P.className "modal-buttons" ]
        [ D.button
            [ Right FinishSigning <$ P.onClick
            , P.className "btn btn-primary", P._type "button"
            ]
            [ D.text $ lookupTranslation "sign" ]
        ]
    ]
  where
    maybeMsg = case state.msg of
        Just msg -> [ D.text msg ]
        Nothing -> []
